/* ------------------------------------------------------------------
   CreditsPage.js  (v2.0 – modernized UI, matches dashboard theme)
   /dashboard/credits — Buy extra AI-credit packs
------------------------------------------------------------------ */
import React, { useState, useContext, useEffect } from 'react'
import { Spinner } from 'react-bootstrap'
import axios from 'axios'
import { UserContext } from '../../../context/UserContext'

const API_BASE = '/api'

/* ─────── pack definitions ─────── */
const PACKS = [
  { credits: 50,  price: 15, label: 'Starter', savings: null,    perCredit: 0.30, desc: 'Great for occasional use' },
  { credits: 75,  price: 20, label: 'Booster', savings: 'Save 10%', perCredit: 0.27, desc: 'Most flexible option'   },
  { credits: 100, price: 25, label: 'Pro',      savings: 'Save 17%', perCredit: 0.25, desc: 'Best value per credit' },
]

/* ─────── Pack Card ─────── */
const PackCard = ({ pack, isSelected, onSelect }) => {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      onClick={() => onSelect(pack)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position    : 'relative',
        background  : '#ffffff',
        border      : `2px solid ${isSelected ? '#2563eb' : hovered ? '#93c5fd' : '#e5e7eb'}`,
        borderRadius: 14,
        padding     : '24px 20px',
        cursor      : 'pointer',
        flex        : '1 1 0',
        minWidth    : 200,
        maxWidth    : 300,
        display     : 'flex',
        flexDirection: 'column',
        alignItems  : 'center',
        textAlign   : 'center',
        transition  : 'all 0.18s ease',
        boxShadow   : isSelected
          ? '0 6px 24px rgba(37,99,235,0.15)'
          : hovered
            ? '0 4px 12px rgba(0,0,0,0.07)'
            : '0 1px 3px rgba(0,0,0,0.05)',
      }}
    >
      {/* Selected indicator */}
      {isSelected && (
        <div style={{
          position    : 'absolute',
          top         : 14,
          right       : 14,
          width       : 22,
          height      : 22,
          borderRadius: '50%',
          background  : '#2563eb',
          display     : 'flex',
          alignItems  : 'center',
          justifyContent: 'center',
          border      : '2px solid #fff',
        }}>
          <i className='fas fa-check' style={{ fontSize: 9, color: '#fff' }} />
        </div>
      )}

      {/* Savings badge */}
      {pack.savings && (
        <div style={{
          position    : 'absolute',
          top         : -12,
          left        : '50%',
          transform   : 'translateX(-50%)',
          background  : '#dcfce7',
          color       : '#16a34a',
          fontSize    : 10,
          fontWeight  : 700,
          padding     : '3px 12px',
          borderRadius: 99,
          whiteSpace  : 'nowrap',
          letterSpacing: '0.04em',
        }}>
          {pack.savings}
        </div>
      )}

      {/* Plan label */}
      <div style={{
        fontSize     : 11,
        fontWeight   : 700,
        textTransform: 'uppercase',
        letterSpacing: '0.09em',
        color        : '#9ca3af',
        marginBottom : 12,
        marginTop    : pack.savings ? 6 : 0,
      }}>
        {pack.label}
      </div>

      {/* Credit count */}
      <div style={{
        fontSize  : 52,
        fontWeight: 900,
        color     : isSelected ? '#2563eb' : '#111827',
        lineHeight: 1,
        transition: 'color 0.15s',
      }}>
        {pack.credits}
      </div>
      <div style={{ fontSize: 13, color: '#9ca3af', fontWeight: 500, marginBottom: 16 }}>
        AI credits
      </div>

      {/* Divider */}
      <div style={{ width: '100%', height: 1, background: '#f3f4f6', marginBottom: 16 }} />

      {/* Price */}
      <div style={{ fontSize: 28, fontWeight: 800, color: '#111827', lineHeight: 1 }}>
        ${pack.price}
      </div>
      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4, marginBottom: 12 }}>
        ${pack.perCredit.toFixed(2)} per credit
      </div>

      {/* Description */}
      <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>
        {pack.desc}
      </div>
    </div>
  )
}

/* ─────── Main Page ─────── */
export default function CreditsPage () {
  const { usage } = useContext(UserContext) ?? {}

  const [selected, setSelected] = useState(PACKS[1])   // default mid-tier
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')

  /* Clean up ?status=success redirect from Stripe */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('status') === 'success') {
      params.delete('status')
      window.history.replaceState({}, '', window.location.pathname)
      window.location.reload()
    }
  }, [])

  const buyCredits = async () => {
    try {
      setLoading(true)
      setError('')
      const { data } = await axios.post(
        `${API_BASE}/billing/credits/buy`,
        { credits: selected.credits },
        { withCredentials: true }
      )
      window.location = data.url
    } catch (err) {
      console.error(err)
      setError(err.response?.data?.error || 'Checkout failed. Please try again.')
      setLoading(false)
    }
  }

  const remaining =
    typeof usage?.ai?.left === 'number' ? usage.ai.left : null

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 6 }}>
          Buy AI Credits
        </h2>
        <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
          Top up your AI credits to keep generating captions, posts, and content ideas.
        </p>
      </div>

      {/* Current balance card */}
      {remaining !== null && (
        <div style={{
          display     : 'flex',
          alignItems  : 'center',
          gap         : 14,
          background  : '#f8fafc',
          border      : '1px solid #e5e7eb',
          borderRadius: 12,
          padding     : '14px 20px',
          marginBottom: 32,
        }}>
          <div style={{
            width       : 40,
            height      : 40,
            borderRadius: '50%',
            background  : '#eff6ff',
            display     : 'flex',
            alignItems  : 'center',
            justifyContent: 'center',
            flexShrink  : 0,
          }}>
            <i className='fas fa-bolt' style={{ color: '#2563eb', fontSize: 16 }} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Current Balance
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#111827', lineHeight: 1.2 }}>
              {remaining.toLocaleString()} <span style={{ fontSize: 13, fontWeight: 500, color: '#6b7280' }}>credits remaining</span>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          background  : '#fef2f2',
          border      : '1px solid #fecaca',
          borderRadius: 10,
          padding     : '12px 16px',
          marginBottom: 24,
          fontSize    : 13,
          color       : '#dc2626',
          fontWeight  : 500,
        }}>
          <i className='fas fa-exclamation-circle me-2' />
          {error}
        </div>
      )}

      {/* Pack cards */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
        {PACKS.map(pack => (
          <PackCard
            key={pack.credits}
            pack={pack}
            isSelected={selected.credits === pack.credits}
            onSelect={setSelected}
          />
        ))}
      </div>

      {/* Summary + CTA */}
      <div style={{
        background  : '#ffffff',
        border      : '1px solid #e5e7eb',
        borderRadius: 12,
        padding     : '20px 24px',
        display     : 'flex',
        alignItems  : 'center',
        justifyContent: 'space-between',
        flexWrap    : 'wrap',
        gap         : 16,
        boxShadow   : '0 1px 3px rgba(0,0,0,0.05)',
      }}>
        <div>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 2 }}>
            Selected pack
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>
            {selected.credits} credits&nbsp;
            <span style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af' }}>
              — ${selected.price} one-time
            </span>
          </div>
          {selected.savings && (
            <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600, marginTop: 2 }}>
              {selected.savings} vs. base rate
            </div>
          )}
        </div>

        <button
          disabled={loading}
          onClick={buyCredits}
          style={{
            display     : 'flex',
            alignItems  : 'center',
            gap         : 8,
            padding     : '12px 28px',
            background  : loading ? '#93c5fd' : '#2563eb',
            color       : '#fff',
            border      : 'none',
            borderRadius: 10,
            fontSize    : 15,
            fontWeight  : 700,
            cursor      : loading ? 'not-allowed' : 'pointer',
            transition  : 'background 0.15s',
            whiteSpace  : 'nowrap',
          }}
          onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#1d4ed8' }}
          onMouseLeave={e => { if (!loading) e.currentTarget.style.background = '#2563eb' }}
        >
          {loading ? (
            <>
              <Spinner animation='border' size='sm' style={{ width: 14, height: 14 }} />
              Processing…
            </>
          ) : (
            <>
              <i className='fas fa-bolt' style={{ fontSize: 13 }} />
              Buy {selected.credits} Credits — ${selected.price}
            </>
          )}
        </button>
      </div>

      {/* Footer note */}
      <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 16, textAlign: 'center' }}>
        Credits are added to your account instantly after purchase and never expire.
      </p>
    </div>
  )
}